import React, { useCallback, useEffect, useState } from 'react';
import { Client, ClientNodeAssignment, isDeliveryNodeType } from '../../types';
import * as api from '../../services/api';
import ClientSelect, { ClientSelection } from './ClientSelect';

/**
 * The Client row that sits at the TOP of every delivery leg-node's config
 * modal — deliberately the first thing in the modal, because who a
 * destination belongs to is the one decision that cannot be undone later.
 *
 * Self-contained on purpose. Each of the five leg-node modals has its own
 * save flow and its own config shape, and threading a client through all of
 * them would mean five different `onSave` signatures. Instead this component
 * owns its own fetch and its own write:
 *
 *   GET  /leg-nodes/:id/client   — current assignment + whether it's locked
 *   PUT  /leg-nodes/:id/client   — assign or re-assign
 *
 * It saves the moment a client is chosen rather than waiting for the modal's
 * "Save Configuration" button, so closing the modal without saving config
 * can't silently drop the assignment.
 *
 * Three states:
 *   locked   — the node has delivered at least one lead. Frozen chip, no
 *              editing, ever. This is the product rule from migration 031.
 *   assigned — has a client, no deliveries yet. Shows the name plus Change.
 *   empty    — legacy node created before clients existed. Shows the picker
 *              and a note that it is required going forward.
 */

interface Props {
  dataBodyId: string;
  legNodeId: string;
  nodeType: string;
  /** Called after a successful assignment so the parent can refresh its row. */
  onAssigned?: (assignment: ClientNodeAssignment, client: Client | null) => void;
}

export default function ClientAssignmentField({
  dataBodyId,
  legNodeId,
  nodeType,
  onAssigned,
}: Props) {
  const [assignment, setAssignment] = useState<ClientNodeAssignment | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [selection, setSelection] = useState<ClientSelection>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getLegNodeClient(dataBodyId, legNodeId);
      setAssignment(res.assignment);
      setClient(res.client);
      setError('');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not load client');
    } finally {
      setLoading(false);
    }
  }, [dataBodyId, legNodeId]);

  useEffect(() => { void load(); }, [load]);

  // Flow-control nodes deliver to nobody, so they carry no client at all.
  if (!isDeliveryNodeType(nodeType)) {return null;}

  async function save() {
    if (!selection) { setError('Pick a client or type a new name'); return; }
    setSaving(true);
    setError('');
    try {
      const saved = await api.setLegNodeClient(dataBodyId, legNodeId, {
        clientId: selection.kind === 'existing' ? selection.clientId : undefined,
        clientName: selection.kind === 'new' ? selection.name : undefined,
      });
      setAssignment(saved);
      setEditing(false);
      setSelection(null);
      // Re-read so the chip shows the canonical name rather than whatever
      // casing was typed, and so `locked` reflects the server's view.
      await load();
      if (onAssigned) {onAssigned(saved, client);}
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to assign client');
    } finally {
      setSaving(false);
    }
  }

  const locked = !!assignment?.locked;
  const name = client?.name ?? null;

  return (
    <div
      style={{
        marginBottom: 16,
        padding: 12,
        background: locked ? '#f9fafb' : '#eff6ff',
        border: `1px solid ${locked ? '#e5e7eb' : '#bfdbfe'}`,
        borderRadius: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            color: locked ? '#6b7280' : '#1d4ed8',
          }}
        >
          Client
        </div>
        {locked && (
          <div style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 5 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
            Locked
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ marginTop: 8, fontSize: 13, color: '#9ca3af' }}>Loading…</div>
      ) : locked ? (
        <>
          <div style={{ marginTop: 6, fontSize: 16, fontWeight: 600, color: '#111827' }}>
            {name || '(client removed)'}
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
            This destination has delivered leads, so its client can no longer be
            changed. To send to someone else, add a new destination.
          </div>
        </>
      ) : editing || !assignment ? (
        <>
          <div style={{ marginTop: 8 }}>
            <ClientSelect
              value={selection}
              onChange={setSelection}
              disabled={saving}
              autoFocus={editing}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={save}
              disabled={saving || !selection}
            >
              {saving ? 'Saving…' : assignment ? 'Change client' : 'Assign client'}
            </button>
            {assignment && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => { setEditing(false); setSelection(null); setError(''); }}
                disabled={saving}
              >
                Cancel
              </button>
            )}
            {!assignment && (
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                Required for new destinations. Locks on the first delivered lead.
              </span>
            )}
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 6 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
            {name || '(client removed)'}
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => { setEditing(true); setSelection(null); }}
          >
            Change
          </button>
        </div>
      )}

      {!loading && !locked && assignment && !editing && (
        <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
          Changeable until the first lead is delivered here.
        </div>
      )}

      {error && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#dc2626' }}>{error}</div>
      )}
    </div>
  );
}
